import React, { useEffect, useRef, useState } from 'react'
import Phaser from 'phaser'


const Recolector = () => {
  // Referencia para almacenar la instancia del juego y destruirla al salir
  const gameInstanceRef = useRef(null)
  
  // Referencia al contenedor HTML para inyectar el juego
  const gameContainerRef = useRef(null)

  // Estado para controlar la visibilidad de la UI de React (Start Screen)
  const [showStartScreen, setShowStartScreen] = useState(true)

  // =========================================================
  // HOOK DE EFECTO PRINCIPAL (Montaje del Juego)
  // =========================================================
  useEffect(() => {
    // Si ya existe una instancia (React StrictMode a veces monta doble), salimos
    if (gameInstanceRef.current) return

    // --- VARIABLES DEL JUEGO (Scope local del closure) ---
    // Se definen aquí para que sean accesibles por todas las funciones de Phaser
    let platformGroup
    let mainGround
    let player
    let stars
    let bombs
    let trophies
    let cursors
    let score = 0
    let scoreText
    let timerText
    let timeLeft = 60
    let gameOver = false
    let starsCollected = 0
    let bg
    let cardsReceived = 0 // Contador de tarjetas (amarilla/roja)
    
    // Bandera para saber si el juego comenzó (lógica)
    let isGameActive = false 

    // --- CONFIGURACIÓN DE PHASER ---
    const config = {
      type: Phaser.AUTO,
      width: '100%',
      height: '100%',
      parent: gameContainerRef.current, // Renderizar dentro del div ref
      backgroundColor: '#121212',
      scale: {
        mode: Phaser.Scale.RESIZE, // Importante para responsive total
        width: '100%',
        height: '100%',
        autoCenter: Phaser.Scale.NO_CENTER
      },
      physics: {
        default: 'arcade',
        arcade: {
          gravity: { y: 300 },
          debug: false
        }
      },
      scene: {
        preload: preload,
        create: create,
        update: update,
        // Extendemos la escena para exponer funciones que necesitamos llamar desde fuera
        extend: {
          startGameLogic: startGameLogic
        }
      }
    }

    // Inicializar el juego
    const game = new Phaser.Game(config)
    gameInstanceRef.current = game

    // =========================================================
    // FUNCIONES DE ESCENA (PHASER)
    // =========================================================

    /**
     * Carga de recursos (imágenes, sprites)
     * Las rutas asumen que 'assets' está en la carpeta 'public' de React
     */
    function preload() {
      this.load.image('sky', '/assets/sky.png')
      this.load.image('ground', '/assets/platform.png')
      this.load.image('star', '/assets/star.png')
      this.load.image('trophy', '/assets/trophy.png')
      this.load.spritesheet('dude', '/assets/dude.png', { frameWidth: 32, frameHeight: 48 })
      this.load.image('card_yellow', '/assets/tarjeta_amarilla.png')
      this.load.image('card_red', '/assets/tarjeta_roja.png')
      
      // Fallback para bomba si no existe imagen específica, usamos tarjeta roja temporalmente o cargamos asset de bomba
      // this.load.image('bomb', '/assets/bomb.png'); 
    }

    /**
     * Creación inicial de objetos en pantalla
     */
    function create() {
      const width = this.scale.width
      const height = this.scale.height

      // 1. Fondo (Background) - Ajuste responsivo manual inicial
      bg = this.add.image(width / 2, height / 2, 'sky')
      const scaleX = width / bg.width
      const scaleY = height / bg.height
      const scale = Math.max(scaleX, scaleY)
      bg.setScale(scale)

      // 2. Plataformas (Suelo)
      platformGroup = this.physics.add.staticGroup()
      mainGround = platformGroup.create(width / 2, height - 32, 'ground')
      mainGround.setDisplaySize(width, 64)
      mainGround.refreshBody()

      // 3. Jugador (Player)
      player = this.physics.add.sprite(100, height - 150, 'dude')
      player.setBounce(0.2)
      player.setCollideWorldBounds(true)
      player.body.setSize(20, 36)
      player.body.setOffset(6, 12)

      // Animaciones del jugador
      if (!this.anims.exists('left')) {
        this.anims.create({
          key: 'left',
          frames: this.anims.generateFrameNumbers('dude', { start: 0, end: 3 }),
          frameRate: 10,
          repeat: -1
        })
      }
      if (!this.anims.exists('turn')) {
        this.anims.create({
          key: 'turn',
          frames: [{ key: 'dude', frame: 4 }],
          frameRate: 20
        })
      }
      if (!this.anims.exists('right')) {
        this.anims.create({
          key: 'right',
          frames: this.anims.generateFrameNumbers('dude', { start: 5, end: 8 }),
          frameRate: 10,
          repeat: -1
        })
      }

      this.physics.add.collider(player, platformGroup)

      // 4. Elementos (Estrellas, Bombas, Trofeos)
      stars = this.physics.add.group({
        key: 'star',
        repeat: 9,
        setXY: { x: 30, y: 0, stepX: (width - 60) / 9 }
      })
      stars.children.iterate((child) => child.setBounceY(Phaser.Math.FloatBetween(0.4, 0.8)))

      bombs = this.physics.add.group()
      trophies = this.physics.add.group()

      // Colisiones y superposiciones
      this.physics.add.collider(stars, platformGroup)
      this.physics.add.overlap(player, stars, collectStar, null, this)
      
      this.physics.add.collider(bombs, platformGroup)
      this.physics.add.collider(player, bombs, hitBomb, null, this)

      this.physics.add.collider(trophies, platformGroup)
      this.physics.add.overlap(player, trophies, collectTrophy, null, this)

      // 5. Interfaz de Usuario (UI dentro del Canvas)
      const textStyle = {
        fontFamily: '"Russo One", sans-serif',
        fontSize: '32px',
        fill: '#FFFFFF',
        stroke: '#000000',
        strokeThickness: 5,
        shadow: { offsetX: 2, offsetY: 2, color: '#000', blur: 2, stroke: true, fill: true }
      }

      scoreText = this.add.text(20, 20, 'PUNTOS: 0', textStyle)
      timerText = this.add.text(width - 20, 20, 'TIEMPO: 60', textStyle).setOrigin(1, 0)

      // 6. Controles
      cursors = this.input.keyboard.createCursorKeys()
      this.input.addPointer(2) // Soporte multitouch

      // Escuchar evento de redimensionamiento de ventana
      this.scale.on('resize', resizeGame, this)

      // Pausar físicas hasta que el usuario pulse "Jugar"
      this.physics.pause()
      isGameActive = false
    }

    /**
     * Bucle principal de juego (se ejecuta en cada frame)
     */
    function update(time, delta) {
      if (gameOver || !isGameActive) return

      // Lógica del Temporizador
      if (timeLeft > 0) {
        timeLeft -= delta / 1000
        timerText.setText(`TIEMPO: ${Math.ceil(timeLeft)}`)

        // Alerta visual de tiempo bajo
        if (timeLeft <= 10) {
          timerText.setTint(0xff5555)
        }
      } else {
        endGame(this, 'time')
      }

      // Lógica de Movimiento
      let moveLeft = false
      let moveRight = false
      let doJump = false

      // Teclado
      if (cursors.left.isDown) moveLeft = true
      else if (cursors.right.isDown) moveRight = true
      if (cursors.up.isDown) doJump = true

      // Táctil (Touch)
      const pointers = [this.input.pointer1, this.input.pointer2]
      pointers.forEach(pointer => {
        if (pointer.isDown) {
          if (pointer.y < this.scale.height / 2) {
            doJump = true // Toque arriba salta
          } else {
            // Toque izquierda/derecha mueve
            if (pointer.x < this.scale.width / 2) moveLeft = true
            else moveRight = true
          }
        }
      })

      // Aplicar velocidad
      if (moveLeft) {
        player.setVelocityX(-300)
        player.anims.play('left', true)
      } else if (moveRight) {
        player.setVelocityX(300)
        player.anims.play('right', true)
      } else {
        player.setVelocityX(0)
        player.anims.play('turn')
      }

      // Aplicar salto
      if (doJump && player.body.touching.down) {
        player.setVelocityY(-220)
      }
    }

    // =========================================================
    // LÓGICA DE JUEGO (HELPERS)
    // =========================================================

    /**
     * Función llamada desde React cuando se pulsa el botón "JUGAR"
     */
    function startGameLogic() {
      isGameActive = true
      this.physics.resume()

      // Evento cíclico para generar trofeos
      this.time.addEvent({
        delay: 10000,
        callback: spawnTrophy,
        callbackScope: this,
        loop: true
      })
    }

    /**
     * Reajusta posiciones cuando la ventana cambia de tamaño
     */
    function resizeGame(gameSize) {
      const width = gameSize.width
      const height = gameSize.height

      this.cameras.main.setViewport(0, 0, width, height)
      this.physics.world.setBounds(0, 0, width, height)

      // Reajustar fondo
      if (bg) {
        bg.setScale(1)
        const scaleX = width / bg.width
        const scaleY = height / bg.height
        const scale = Math.max(scaleX, scaleY)
        bg.setScale(scale)
        bg.setPosition(width / 2, height / 2)
      }

      // Reajustar suelo
      if (mainGround) {
        mainGround.setPosition(width / 2, height - 32)
        mainGround.setDisplaySize(width, 64)
        mainGround.body.reset(width / 2, height - 32)
        mainGround.refreshBody()
      }

      // Reajustar UI
      if (scoreText) scoreText.setPosition(20, 20)
      if (timerText) {
        timerText.setPosition(width - 20, 20)
        // Fuente responsive
        const newSize = width < 450 ? '20px' : '32px'
        timerText.setFontSize(newSize)
        scoreText.setFontSize(newSize)
      }

      // Devolver al jugador al área de juego si quedó fuera
      if (player) {
        if (player.y > height - 50 || player.x > width) {
          player.setPosition(
            Phaser.Math.Clamp(player.x, 50, width - 50),
            height - 100
          )
          player.setVelocity(0, 0)
        }
      }

      // Rescatar elementos caídos fuera de pantalla
      const rescueGroup = (group) => {
        if (!group) return
        group.children.iterate(child => {
          if (child && child.active && child.y > height) {
            child.setY(0)
            child.setVelocityY(0)
            child.setX(Phaser.Math.Between(0, width))
          }
        })
      }
      rescueGroup(stars)
      rescueGroup(bombs)
      rescueGroup(trophies)
    }

    /**
     * Lógica al recolectar estrella
     */
    function collectStar(player, star) {
      star.disableBody(true, true)
      score += 1
      scoreText.setText('PUNTOS: ' + score)
      starsCollected++

      // Regenerar estrellas si se acaban
      if (stars.countActive(true) === 0) {
        stars.children.iterate((child) => {
          child.enableBody(true, Phaser.Math.Between(0, this.scale.width), 0, true, true)
        })
      }

      // Agregar más estrellas cada 5 recolectadas
      if (starsCollected % 5 === 0) {
        const width = this.scale.width
        for (let i = 0; i < 5; i++) {
          const x = Phaser.Math.Between(30, width - 30)
          const newStar = stars.create(x, 0, 'star')
          newStar.setBounceY(Phaser.Math.FloatBetween(0.4, 0.8))
          newStar.setCollideWorldBounds(true)
          newStar.setVelocity(Phaser.Math.Between(-50, 50), 20)
        }
      }

      // Lógica de Tarjetas/Bombas: Cada 12 estrellas lanza 2 tarjetas
      if (starsCollected % 12 === 0 && starsCollected > 0) {
        for (let i = 0; i < 2; i++) {
          // Si no tienes tarjetas, lanza amarilla. Si ya tienes 1, lanza roja.
          const texture = (cardsReceived === 0) ? 'card_yellow' : 'card_red'
          const bomb = bombs.create(Phaser.Math.Between(0, this.scale.width), 16, texture)
          bomb.body.setSize(12, 16)
          bomb.setBounce(1)
          bomb.setCollideWorldBounds(true)
          bomb.setVelocity(Phaser.Math.Between(-200, 200), 100)
        }
      }
    }

    /**
     * Lógica al chocar con bomba/tarjeta
     */
    function hitBomb(player, bomb) {
      bomb.disableBody(true, true)
      cardsReceived++
      this.cameras.main.shake(200, 0.01)

      player.setTint(0xff0000)
      this.time.delayedCall(500, () => {
        if (!gameOver) player.clearTint()
      })

      // Primera Tarjeta: Amonestación
      if (cardsReceived === 1) {
        let warnText = this.add.text(player.x, player.y - 60, '¡AMONESTADO!', {
          fontFamily: '"Russo One", sans-serif',
          fontSize: '32px',
          fill: '#FFFF00',
          stroke: '#000000',
          strokeThickness: 6,
          shadow: { offsetX: 3, offsetY: 3, color: '#000', blur: 0, fill: true }
        }).setOrigin(0.5)

        this.tweens.add({
          targets: warnText,
          y: player.y - 150,
          scale: 1.2,
          alpha: 0,
          duration: 2000,
          ease: 'Power2',
          onComplete: () => warnText.destroy()
        })

        // Cambiar todas las bombas activas a tarjetas rojas
        bombs.children.iterate((child) => {
          if (child.active) child.setTexture('card_red')
        })
      }

      // Segunda Tarjeta: Expulsión (Game Over)
      if (cardsReceived >= 2) {
        endGame(this, 'bomb')
      }
    }

    /**
     * Generar Trofeo (Puntos extra)
     */
    function spawnTrophy() {
      if (gameOver || !isGameActive) return
      const width = this.scale.width
      const x = Phaser.Math.Between(50, width - 50)
      const trophy = trophies.create(x, 0, 'trophy')

      trophy.setBounce(1)
      trophy.setCollideWorldBounds(true)
      trophy.setVelocity(Phaser.Math.Between(-200, 200), 20)

      // Desaparece a los 5 segundos
      this.time.delayedCall(5000, () => { if (trophy.active) trophy.destroy() })
    }

    /**
     * Recolectar Trofeo
     */
    function collectTrophy(player, trophy) {
      trophy.destroy()
      score += 10
      scoreText.setText('PUNTOS: ' + score)

      // Efecto de texto flotante
      let txt = this.add.text(player.x, player.y - 20, '+10', {
        fontFamily: '"Russo One", sans-serif',
        fontSize: '32px',
        fill: '#00FF00',
        stroke: '#000',
        strokeThickness: 4
      }).setOrigin(0.5)

      this.tweens.add({ targets: txt, y: player.y - 80, alpha: 0, duration: 1000, onComplete: () => txt.destroy() })
    }

    /**
     * Fin del Juego
     */
    function endGame(scene, reason) {
      if (gameOver) return
      gameOver = true
      scene.physics.pause()
      player.setTint(0x555555)
      player.anims.play('turn')

      const width = scene.scale.width
      const height = scene.scale.height

      // Fondo oscuro semitransparente
      let bgRect = scene.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.7)
      bgRect.setDepth(10)

      // Mensaje Principal
      const gameOverText = scene.add.text(width / 2, height / 2 - 40, 'Gracias por participar', {
        fontFamily: '"Russo One", sans-serif',
        fontSize: (width < 600) ? '32px' : '48px',
        fill: '#FFFFFF',
        stroke: '#FF0000',
        strokeThickness: 2,
        shadow: { offsetX: 4, offsetY: 4, color: '#000000', blur: 5, stroke: true, fill: true }
      }).setOrigin(0.5).setDepth(11)

      // Subtítulo y reinicio
      scene.add.text(width / 2, height / 2 + 40, `Puntuación Final: ${score}\nToca para reiniciar`, {
        fontFamily: '"Russo One", sans-serif',
        fontSize: '24px',
        fill: '#CCCCCC',
        align: 'center'
      }).setOrigin(0.5).setDepth(11)

      // Reiniciar al hacer click/tap
      scene.input.on('pointerdown', () => {
         // Opción A: Recargar página (como en el original)
         window.location.reload()
         
         // Opción B (Más "React"): Reiniciar escena solamente
         // scene.scene.restart()
         // score = 0
         // timeLeft = 60
         // gameOver = false
         // ... (habría que resetear todas las variables let locales)
      })
    }

    // Limpieza al desmontar el componente (Return del useEffect)
    return () => {
      game.destroy(true)
      gameInstanceRef.current = null
    }
  }, []) // Array vacío: Solo se ejecuta al montar

  // =========================================================
  // HANDLERS DE UI REACT
  // =========================================================
  
  const handleStartGame = () => {
    // 1. Ocultar la UI de React
    setShowStartScreen(false)

    // 2. Comunicarse con Phaser para iniciar la lógica
    if (gameInstanceRef.current) {
      const scene = gameInstanceRef.current.scene.getScene('default') // Obtiene la escena activa
      if (scene && scene.startGameLogic) {
        scene.startGameLogic()
      }
    }
  }

  // =========================================================
  // RENDERIZADO JSX
  // =========================================================
  return (
    <div className="recolector">
      {/* Contenedor del Juego Phaser */}
      <div className="recolector__canvas-container" ref={gameContainerRef} />

      {/* Pantalla de Inicio (UI Overlay) */}
      <div className={`recolector__start-screen ${!showStartScreen ? 'recolector__start-screen--hidden' : ''}`}>
        <div className="recolector__info-box">
          <div style={{ fontSize: '40px', marginBottom: '10px' }}>⚽🏆</div>
          <p>
            Tienes <strong>60 segundos</strong> para recolectar la mayor cantidad de balones.
            <br /><br />
            <span className="recolector__highlight">⚠️ Si recibes 2 tarjetas, serás expulsado.</span>
          </p>
          <p style={{ fontSize: '14px', color: '#888', marginBottom: '25px' }}>
            <i>Desliza tu dedo en la pantalla para moverte.</i>
          </p>
          <button className="recolector__start-btn" onClick={handleStartGame}>¡JUGAR!</button>
        </div>
      </div>

      {/* Aviso de Giro (Solo visible en Portrait por CSS) */}
      <div className="recolector__turn-device">
        <div className="icon">📱</div>
        <h2>Gira tu dispositivo</h2>
        <p>Juega en horizontal para la mejor experiencia.</p>
      </div>
    </div>
  )
}

export { Recolector };









// import React, { useEffect, useRef } from 'react'
// import Phaser from 'phaser'


// const Recolector = () => {
//     // Usamos useRef para mantener una referencia al juego sin provocar re-renderizados
//     const gameInstanceRef = useRef(null);

//     useEffect(() => {
//         // --- CONFIGURACIÓN E INICIALIZACIÓN ---
        
//         // Definimos la configuración dentro del useEffect para asegurar que el DOM existe
//         const configuracionJuego = {
//             type: Phaser.AUTO,
//             width: 800,
//             height: 600,
//             parent: 'contenedor-juego', // ID del div donde se montará Phaser
//             physics: {
//                 default: 'arcade',
//                 arcade: {
//                     gravity: { y: 300 },
//                     debug: false
//                 }
//             },
//             scene: {
//                 preload: preloadGame,
//                 create: createGame,
//                 update: updateGame
//             },
//             scale: {
//                 mode: Phaser.Scale.FIT,
//                 autoCenter: Phaser.Scale.CENTER_BOTH
//             }
//         };

//         // Variables globales del ámbito del juego (scope)
//         let player;
//         let stars;
//         let bombs;
//         let trophies;
//         let cursors;
//         let score = 0;
//         let scoreText;
//         let timerText;
//         let timeLeft = 60;
//         let gameOver = false;
//         let starsCollected = 0;

//         // --- FUNCIONES DE PHASER ---

//         function preloadGame() {
//             // IMPORTANTE: Al estar en la carpeta public, accedemos con la ruta raíz '/'
//             this.load.image('sky', '/assets/sky.png');
//             this.load.image('ground', '/assets/platform.png');
//             this.load.image('star', '/assets/star.png');
//             this.load.image('bomb', '/assets/bomb.png'); // Asegúrate que exista o usa el fallback
//             this.load.image('trophy', '/assets/trophy.png');
//             this.load.spritesheet('dude', '/assets/dude.png', { frameWidth: 32, frameHeight: 48 });
            
//             // Si tienes las tarjetas, cárgalas aquí también
//             this.load.image('card_yellow', '/assets/tarjeta_amarilla.png');
//             this.load.image('card_red', '/assets/tarjeta_roja.png');
//         }

//         function createGame() {
//             // Fondo
//             this.add.image(400, 300, 'sky');

//             // Plataformas
//             const platforms = this.physics.add.staticGroup();
//             platforms.create(400, 568, 'ground').setScale(2).refreshBody();

//             // Jugador
//             player = this.physics.add.sprite(100, 450, 'dude');
//             player.setBounce(0.2);
//             player.setCollideWorldBounds(true);

//             // Animaciones
//             if (!this.anims.exists('left')) {
//                 this.anims.create({
//                     key: 'left',
//                     frames: this.anims.generateFrameNumbers('dude', { start: 0, end: 3 }),
//                     frameRate: 12,
//                     repeat: -1
//                 });
//             }

//             if (!this.anims.exists('turn')) {
//                 this.anims.create({
//                     key: 'turn',
//                     frames: [{ key: 'dude', frame: 4 }],
//                     frameRate: 20
//                 });
//             }

//             if (!this.anims.exists('right')) {
//                 this.anims.create({
//                     key: 'right',
//                     frames: this.anims.generateFrameNumbers('dude', { start: 5, end: 8 }),
//                     frameRate: 12,
//                     repeat: -1
//                 });
//             }

//             // Estrellas
//             stars = this.physics.add.group({
//                 key: 'star',
//                 repeat: 11,
//                 setXY: { x: 12, y: 0, stepX: 70 }
//             });

//             stars.children.iterate((child) => {
//                 child.setBounceY(Phaser.Math.FloatBetween(0.1, 0.9));
//             });

//             // Bombas y Trofeos (Grupos vacíos iniciales)
//             bombs = this.physics.add.group();
//             trophies = this.physics.add.group();

//             // Colisiones
//             this.physics.add.collider(player, platforms);
//             this.physics.add.collider(stars, platforms);
//             this.physics.add.collider(bombs, platforms);
//             this.physics.add.collider(trophies, platforms);

//             // Superposiciones (Overlaps)
//             this.physics.add.overlap(player, stars, collectStar, null, this);
//             this.physics.add.collider(player, bombs, hitBomb, null, this);
//             this.physics.add.overlap(player, trophies, collectTrophy, null, this);

//             // Inputs y Textos
//             cursors = this.input.keyboard.createCursorKeys();
//             scoreText = this.add.text(16, 16, 'Puntuación: 0', { fontFamily: 'sans-serif', fontSize: '30px', fill: '#282d60', fontStyle: 'bold' });
//             timerText = this.add.text(600, 16, 'Tiempo: 60', { fontFamily: 'sans-serif', fontSize: '30px', fill: '#282d60', fontStyle: 'bold' });

//             // Evento de Trofeo
//             this.time.addEvent({
//                 delay: 10000,
//                 callback: spawnTrophy,
//                 callbackScope: this,
//                 loop: true
//             });
//         }

//         function updateGame(time, delta) {
//             if (gameOver) return;

//             // Lógica del timer
//             if (timeLeft > 0) {
//                 timeLeft -= delta / 1000;
//                 timerText.setText(`Tiempo: ${Math.ceil(timeLeft)}`);
//             } else {
//                 endGame(this, 'time');
//             }

//             // Controles
//             if (cursors.left.isDown) {
//                 player.setVelocityX(-300);
//                 player.anims.play('left', true);
//             } else if (cursors.right.isDown) {
//                 player.setVelocityX(300);
//                 player.anims.play('right', true);
//             } else {
//                 player.setVelocityX(0);
//                 player.anims.play('turn');
//             }

//             if (cursors.up.isDown && player.body.touching.down) {
//                 player.setVelocityY(-270);
//             }
//         }

//         // --- LÓGICA DEL JUEGO (Helpers) ---

//         function collectStar(player, star) {
//             star.disableBody(true, true);
//             score += 1;
//             scoreText.setText('Puntuación: ' + score);
//             starsCollected++;

//             if (stars.countActive(true) === 0) {
//                  stars.children.iterate((child) => {
//                     child.enableBody(true, child.x, 0, true, true);
//                 });
//             }

//             if (starsCollected % 3 === 0) {
//                  // Regenerar estrellas si quieres lógica infinita, o ajustar según tu script original
//             }

//             // Lógica de bombas (simplificada para el ejemplo)
//             if (starsCollected % 20 === 0) {
//                 const x = (player.x < 400) ? Phaser.Math.Between(400, 800) : Phaser.Math.Between(0, 400);
//                 const bomb = bombs.create(x, 16, 'bomb');
//                 bomb.setBounce(1);
//                 bomb.setCollideWorldBounds(true);
//                 bomb.setVelocity(Phaser.Math.Between(-200, 200), 20);
//             }
//         }

//         function hitBomb(player, bomb) {
//             endGame(this, 'bomb');
//         }

//         function spawnTrophy() {
//             if (gameOver) return;
//             const x = Phaser.Math.Between(0, 800);
//             const trophy = trophies.create(x, 0, 'trophy');
//             trophy.setBounceY(Phaser.Math.FloatBetween(0.4, 0.8));
//             trophy.setCollideWorldBounds(true);
//              // Destruir trofeo después de 5 segs si no se agarra
//             this.time.delayedCall(5000, () => { if (trophy.active) trophy.destroy(); });
//         }

//         function collectTrophy(player, trophy) {
//             trophy.destroy();
//             score += 100;
//             scoreText.setText('Puntuación: ' + score);
//         }

//         function endGame(scene, reason) {
//             if (gameOver) return;
//             gameOver = true;
//             scene.physics.pause();
//             player.setTint(0xff0000);
//             player.anims.play('turn');

//             let message = (reason === 'time') ? '¡TIEMPO!' : 'GAME OVER';
//             const width = scene.scale.width;
            
//             const gameOverText = scene.add.text(width / 2, 300, message, { 
//                 fontFamily: 'sans-serif', 
//                 fontSize: '69px', 
//                 fill: '#cc3723', 
//                 fontStyle: 'bold' 
//             }).setOrigin(0.5);
//         }

//         // --- INICIALIZACIÓN DE LA INSTANCIA ---
        
//         // Verificamos que no exista una instancia previa para evitar duplicados (común en React StrictMode)
//         if (!gameInstanceRef.current) {
//             gameInstanceRef.current = new Phaser.Game(configuracionJuego);
//         }

//         // --- CLEANUP (Limpieza) ---
//         // Esto se ejecuta cuando cambias de página en React o desmontas el componente
//         return () => {
//             if (gameInstanceRef.current) {
//                 gameInstanceRef.current.destroy(true); // true = destruir también el canvas
//                 gameInstanceRef.current = null;
//             }
//         };

//     }, []); // El array vacío asegura que esto solo corra una vez al montar

//     // Renderizamos simplemente el contenedor donde Phaser se inyectará
//     return (
//         <div 
//             id="contenedor-juego" 
//             style={{ 
//                 display: 'flex', 
//                 justifyContent: 'center', 
//                 alignItems: 'center', 
//                 height: '100vh', 
//                 width: '100%' 
//             }}
//         />
//     );
// };

// export { Recolector };